/**
 * CELL AURA — GSAP ScrollTrigger wiring for the homepage.
 * Follows shopify-3d-experience skill rules:
 *  - register plugin once
 *  - respect prefers-reduced-motion
 *  - matchMedia gate at 750px (theme breakpoint)
 *  - single RAF loop (GSAP ticker only)
 *  - teardown on shopify:section:unload (theme editor + SRA)
 *  - refresh after document.fonts.ready
 *  - animates transform/opacity only
 */
(function () {
  'use strict';

  var BP = '(min-width: 750px)';
  var triggers = [];

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Respect user preference — snap visibility without motion.
      document.querySelectorAll('[data-scroll-reveal]').forEach(function (el) {
        el.style.opacity = 1;
        el.style.transform = 'none';
      });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    // --- Generic reveal: every [data-scroll-reveal] fades + lifts once
    gsap.utils.toArray('[data-scroll-reveal]').forEach(function (el) {
      gsap.set(el, { opacity: 0, y: 32 });
      var t = ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(el, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' });
        }
      });
      triggers.push(t);
    });

    // --- Section headings: subtle scrub parallax on desktop only
    ScrollTrigger.matchMedia({
      '(min-width: 750px)': function () {
        gsap.utils.toArray('.seed-ingredients__title, .seed-synergy__title').forEach(function (el) {
          var t = gsap.fromTo(
            el,
            { y: 20 },
            {
              y: -20,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.5
              }
            }
          );
          triggers.push(t.scrollTrigger);
        });

        // Ingredient grid: staggered reveal in rows
        var cards = gsap.utils.toArray('.seed-ingredients__card');
        if (cards.length) {
          gsap.set(cards, { opacity: 0, y: 40 });
          var t = ScrollTrigger.batch(cards, {
            start: 'top 85%',
            onEnter: function (batch) {
              gsap.to(batch, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                stagger: 0.08,
                ease: 'power2.out',
                overwrite: true
              });
            }
          });
          (Array.isArray(t) ? t : [t]).forEach(function (st) {
            if (st) triggers.push(st);
          });
        }
      }
    });

    // --- Technology section: split words + reveal + stat counter
    document.querySelectorAll('.seed-technology').forEach(function (section) {
      var titleEl = section.querySelector('[data-split-words]');
      if (titleEl && !titleEl.dataset.splitDone) {
        var words = titleEl.textContent.trim().split(/\s+/);
        titleEl.innerHTML = words.map(function (w, i) {
          return '<span class="word" style="--word-index:' + i + '">' + w + '</span>';
        }).join(' ');
        titleEl.dataset.splitDone = '1';
      }

      var statEl = section.querySelector('[data-count-to]');
      var statTarget = statEl ? parseFloat(statEl.dataset.countTo) : NaN;
      var statSuffix = '';
      if (statEl) {
        var raw = statEl.dataset.countTo.trim();
        var m = raw.match(/[^\d.\-]+$/);
        if (m) statSuffix = m[0];
      }

      var t = ScrollTrigger.create({
        trigger: section,
        start: 'top 75%',
        once: true,
        onEnter: function () {
          section.setAttribute('data-revealed', '');
          if (statEl && !isNaN(statTarget)) {
            var counter = { v: 0 };
            gsap.to(counter, {
              v: statTarget,
              duration: 1.6,
              ease: 'power2.out',
              delay: 0.6,
              onUpdate: function () {
                var val = counter.v;
                var display = statTarget % 1 === 0 ? Math.round(val).toString() : val.toFixed(1);
                statEl.textContent = display + statSuffix;
              }
            });
          }
        }
      });
      triggers.push(t);

      // Subtle parallax on the image
      var img = section.querySelector('.seed-technology__image img');
      if (img) {
        var p = gsap.fromTo(img, { yPercent: -3 }, {
          yPercent: 5, ease: 'none',
          scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
        });
        if (p.scrollTrigger) triggers.push(p.scrollTrigger);
      }
    });

    // --- Parallax banner(s)
    document.querySelectorAll('[data-parallax-banner]').forEach(function (section) {
      var media = section.querySelector('[data-parallax-media]');
      var heading = section.querySelector('[data-parallax-heading]');
      if (media) {
        var mt = gsap.fromTo(media, { yPercent: -8 }, {
          yPercent: 12,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
        });
        if (mt.scrollTrigger) triggers.push(mt.scrollTrigger);
      }
      if (heading) {
        var ht = gsap.fromTo(heading, { y: 40, opacity: 0.7 }, {
          y: -30, opacity: 1, ease: 'none',
          scrollTrigger: { trigger: section, start: 'top 85%', end: 'bottom top', scrub: 0.8 }
        });
        if (ht.scrollTrigger) triggers.push(ht.scrollTrigger);
      }
    });

    // Refresh once web fonts are settled so measurements are accurate.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        ScrollTrigger.refresh();
      });
    }
  }

  function destroy() {
    triggers.forEach(function (t) {
      try { t.kill(); } catch (e) {}
    });
    triggers = [];
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
    }
  }

  function boot() {
    if (window.__cellAuraScrollBooted) return;
    window.__cellAuraScrollBooted = true;
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor + Section Rendering API lifecycle.
  document.addEventListener('shopify:section:load', function () {
    destroy();
    window.__cellAuraScrollBooted = false;
    boot();
  });
  document.addEventListener('shopify:section:unload', destroy);
})();
